import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Loader, ScanLine, CheckCircle, AlertCircle } from 'lucide-react';

const ZXING_CDN = 'https://cdn.jsdelivr.net/npm/@zxing/library@0.19.1/umd/index.min.js';

function loadZXing() {
  return new Promise((resolve, reject) => {
    if (window.ZXing) { resolve(window.ZXing); return; }
    const existing = document.getElementById('zxing-script');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.ZXing));
      existing.addEventListener('error', () => reject(new Error('ZXing load failed')));
      return;
    }
    const script = document.createElement('script');
    script.id = 'zxing-script';
    script.src = ZXING_CDN;
    script.onload = () => resolve(window.ZXing);
    script.onerror = () => reject(new Error('ZXing load failed'));
    document.head.appendChild(script);
  });
}

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = 880; osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
    osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.2);
  } catch { }
}

export default function BarcodeScanner({ onScan, onClose, title = 'Scan Barcode' }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const cooldownRef = useRef(false);
  const trackRef = useRef(null);
  const [status, setStatus] = useState('loading');
  const [cameras, setCameras] = useState([]);
  const [selectedCamId, setSelectedCamId] = useState(null);
  const [lastCode, setLastCode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [torchOn, setTorchOn] = useState(false);

  const stopReader = useCallback(() => {
    if (readerRef.current) {
      try { readerRef.current.reset(); } catch { }
      readerRef.current = null;
    }
  }, []);

  const startWithCamera = useCallback(async (camId) => {
    setStatus('loading');
    setErrorMsg('');
    stopReader();
    try {
      const ZXing = await loadZXing();

      const hints = new Map();
      hints.set(ZXing.DecodeHintType.POSSIBLE_FORMATS, [
        ZXing.BarcodeFormat.CODE_128,
        ZXing.BarcodeFormat.CODE_39,
        ZXing.BarcodeFormat.EAN_13,
        ZXing.BarcodeFormat.EAN_8,
        ZXing.BarcodeFormat.QR_CODE,
        ZXing.BarcodeFormat.DATA_MATRIX,
        ZXing.BarcodeFormat.UPC_A,
      ]);
      hints.set(ZXing.DecodeHintType.TRY_HARDER, true);

      const reader = new ZXing.BrowserMultiFormatReader(hints);
      readerRef.current = reader;

      // listVideoInputDevices is an INSTANCE method in 0.19.x (not static)
      const devices = await reader.listVideoInputDevices();
      setCameras(devices);

      let chosenId = camId;
      if (!chosenId && devices.length) {
        const back = devices.find(d => /back|rear|environment/i.test(d.label));
        chosenId = back?.deviceId || devices[devices.length - 1]?.deviceId;
      }
      setSelectedCamId(chosenId || null);
      setStatus('scanning');

      await reader.decodeFromVideoDevice(
        chosenId || undefined,
        videoRef.current,
        (result) => {
          if (!result || cooldownRef.current) return;
          cooldownRef.current = true;
          const code = result.getText();
          setLastCode(code);
          setStatus('success');
          playBeep();
          onScan(code);
          setTimeout(() => {
            setStatus('scanning');
            setLastCode('');
            cooldownRef.current = false;
          }, 1500);
        }
      );

      if (videoRef.current?.srcObject) {
        const tracks = videoRef.current.srcObject.getVideoTracks();
        if (tracks.length) trackRef.current = tracks[0];
      }

    } catch (err) {
      console.error('Scanner error:', err);
      let msg = 'Could not start camera.';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')
        msg = 'Camera permission denied. Please allow camera access in browser settings.';
      else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError')
        msg = 'No camera found on this device.';
      else if (err.name === 'NotReadableError')
        msg = 'Camera is already in use by another app. Close it and try again.';
      else if (err.message?.includes('ZXing') || err.message?.includes('load'))
        msg = 'Scanner library failed to load. Check your internet connection.';
      setErrorMsg(msg);
      setStatus('error');
    }
  }, [onScan, stopReader]);

  useEffect(() => {
    startWithCamera(null);
    return () => stopReader();
  }, []);

  const handleCameraSwitch = (deviceId) => {
    setSelectedCamId(deviceId);
    startWithCamera(deviceId);
  };

  const toggleTorch = async () => {
    if (!trackRef.current) return;
    try {
      const next = !torchOn;
      await trackRef.current.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch { }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-900">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-amber-400" />
            <span className="text-white font-semibold text-sm">{title}</span>
          </div>
          <button onClick={() => { stopReader(); onClose(); }}
            className="p-1.5 text-gray-400 hover:text-white rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Video viewport */}
        <div className="relative bg-black" style={{ aspectRatio: '4/3' }}>
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />

          {status === 'scanning' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="absolute inset-0 bg-black/20" />
              <div className="relative w-56 h-36" style={{ zIndex: 1 }}>
                <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-amber-400" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-amber-400" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-amber-400" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-amber-400" />
                <div className="absolute left-2 right-2 h-px bg-amber-400 opacity-80"
                  style={{ top: '50%', animation: 'bc_scan 1.8s ease-in-out infinite alternate' }} />
              </div>
            </div>
          )}

          {status === 'loading' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 gap-3">
              <Loader className="w-8 h-8 text-amber-400 animate-spin" />
              <p className="text-white text-sm">Starting camera...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-green-500/40 gap-2">
              <CheckCircle className="w-12 h-12 text-white" />
              <span className="text-white font-bold text-sm bg-black/50 px-3 py-1 rounded-full font-mono">
                {lastCode}
              </span>
            </div>
          )}

          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/85 p-5 gap-3">
              <AlertCircle className="w-10 h-10 text-red-400" />
              <p className="text-white text-sm text-center leading-relaxed">{errorMsg}</p>
              <button onClick={() => startWithCamera(selectedCamId)}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium">
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="px-4 py-3 bg-gray-900 space-y-2">
          {cameras.length > 1 && (
            <select value={selectedCamId || ''}
              onChange={e => handleCameraSwitch(e.target.value)}
              className="w-full bg-gray-800 text-white text-xs rounded-lg px-3 py-2 border border-gray-700 focus:outline-none focus:ring-1 focus:ring-amber-500">
              {cameras.map(cam => (
                <option key={cam.deviceId} value={cam.deviceId}>
                  {cam.label || `Camera ${cam.deviceId.slice(0, 12)}...`}
                </option>
              ))}
            </select>
          )}
          <div className="flex items-center justify-between">
            <button onClick={toggleTorch}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                torchOn ? 'bg-amber-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}>
              🔦 {torchOn ? 'Torch On' : 'Torch'}
            </button>
            <p className="text-gray-400 text-xs">
              {status === 'scanning' && 'Hold barcode in frame'}
              {status === 'success' && '✓ Ready for next scan'}
            </p>
          </div>
        </div>
      </div>

      <style>{`@keyframes bc_scan { from{top:15%} to{top:80%} }`}</style>
    </div>
  );
}
